import { SoundId } from "../../engine/things/sounds/soundId";
import { SoundLoader } from "./soundLoader";

export enum UiSound {
	CLICK = "CLICK",
	WEAPON_SWITCH = "WEAPON_SWITCH",
	GAME_START = "GAME_START",
	GAME_OVER = "GAME_OVER",
	COIN = "COIN",
}

// COIN reuses the in-game gold-pickup sound so the buy/sell coin stays in sync with it.
const UI_SOUND_FILE: Record<UiSound, () => string> = {
	[UiSound.CLICK]: () => "switch_1.ogg",
	[UiSound.WEAPON_SWITCH]: () => "switch_2.ogg",
	[UiSound.GAME_START]: () => "523763__lilmati__select-granted-06.wav",
	[UiSound.GAME_OVER]: () => "270467__littlerobotsoundfactory__jingle_lose_00.wav",
	[UiSound.COIN]: () => SoundLoader.getFileNameForSoundId(SoundId.PICKUP_GOLD)!,
};

/**
 * Plays fire-and-forget, non-positional UI sounds outside the turn loop. Unlike SoundManager
 * (engine-driven board Sounds, batch-played per frame), each call here spins up a one-shot
 * source→gain→destination chain and lets it expire on its own.
 */
export class UiSoundPlayer {
	private volumeLevel = 1.0;

	constructor(
		private readonly audioContext: AudioContext,
		private readonly soundLoader: SoundLoader,
	) {}

	setVolumeLevel(level: number): void {
		this.volumeLevel = level;
	}

	async play(sound: UiSound): Promise<void> {
		if (this.volumeLevel <= 0) return;
		// UI sounds fire inside user gestures (clicks/keys), so resume() unlocks a context
		// still suspended from page load.
		this.audioContext.resume();

		const filename = UI_SOUND_FILE[sound]();
		const buffer = await this.soundLoader.loadAudioBuffer(filename);
		if (!buffer) return;

		const source = this.audioContext.createBufferSource();
		source.buffer = buffer;
		const gainNode = this.audioContext.createGain();
		gainNode.gain.value = this.volumeLevel * SoundLoader.getVolumeBoostForFile(filename);
		source.connect(gainNode);
		gainNode.connect(this.audioContext.destination);
		source.onended = () => {
			source.disconnect();
			gainNode.disconnect();
		};
		source.start();
	}
}

let player: UiSoundPlayer | null = null;

export function initUiSounds(audioContext: AudioContext, soundLoader: SoundLoader): void {
	player = new UiSoundPlayer(audioContext, soundLoader);
}

export function setUiSoundVolume(level: number): void {
	player?.setVolumeLevel(level);
}

export function playUiSound(sound: UiSound): void {
	void player?.play(sound);
}
